import React, { useState, useEffect, useMemo } from "react";
import { Save as SaveIcon, Cancel as CancelIcon, Lock as LockIcon, Warning as WarningIcon } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import { DateTime } from "luxon";
import { useForm, FormProvider } from "react-hook-form";
import { formatPlatform } from "utils/formatters/PlatformFormatters";
import type { Subscription, Plan, EntitlementConfiguration } from "client/api-v2";
import { LifecycleType, ProductPlatform, Tier, ComplimentaryType, AutoRenewPeriod } from "client/api-v2";
import type { OctopusCloudRegionDto } from "client/api/OctopusCloudRegionApi";
import type { UserDto } from "client/api/UsersApi";
import type { BaseEntitlementsFormInputs } from "components/Entitlements/EntitlementFormInputs";
import { UserSelector } from "components/UserSelector/UserSelector";
import { FormDatePicker } from "components/form/FormDatePicker";
import { PlanService, SubscriptionService } from "../services/subscriptionService";
import { applyTrialOverrides, billableTiers, lifecycleSupportsEntitlementOverrides } from "../utils/plan-utils";
import { CloudPlatformFields, CloudMaintenanceWindowFields } from "./platform/CloudPlatformFields";
import { EntitlementEditor } from "./shared/EntitlementEditor";
import { OverridesDisplay } from "./shared/OverridesDisplay";
import { PlanSelector } from "./shared/PlanSelector";
import { ZUORA_KEY_REGEX, ZUORA_KEY_VALIDATION_MESSAGE } from "./zuoraKeyValidation";

export interface SubscriptionFormData extends BaseEntitlementsFormInputs {
  platform: ProductPlatform | "";
  planId?: string;
  plan?: Plan;
  friendlyName: string;
  user?: UserDto;
  termEndDate?: DateTime;
  // Cloud-specific fields (optional, only for Cloud platform)
  dnsPrefix?: string;
  cloudRegion?: OctopusCloudRegionDto;
  outageWindowStartUtc?: DateTime;
  outageWindowEndUtc?: DateTime;
  company?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  complimentaryType?: ComplimentaryType;
  autoRenewPeriod?: AutoRenewPeriod;
  createLifecycleType?: LifecycleType;
  zuoraSubscriptionKey?: string;
}

interface SubscriptionFormProps {
  mode: "create" | "edit";
  initialData?: Partial<Subscription>;
  selectedPlan?: Plan;
  onSubmit: (data: SubscriptionFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
  lockedTier?: Tier;
  lifecycleType?: LifecycleType;
  createLifecycleType?: LifecycleType;
  onCreateLifecycleTypeChange?: (type: LifecycleType) => void;
}

function getEffectiveLockedTier(
  mode: "create" | "edit",
  createLifecycleType: LifecycleType | undefined,
  lockedTier: Tier | undefined
): Tier | undefined {
  if (mode !== "create") return lockedTier;
  switch (createLifecycleType) {
    case LifecycleType.Trial:
      return Tier.Enterprise;
    case LifecycleType.Paid:
      return undefined;
    case LifecycleType.Complimentary:
      return undefined;
    default:
      return Tier.Free;
  }
}

export function SubscriptionForm({
  mode,
  initialData,
  selectedPlan,
  onSubmit,
  onCancel,
  loading = false,
  disabled = false,
  lockedTier,
  lifecycleType,
  createLifecycleType,
  onCreateLifecycleTypeChange,
}: SubscriptionFormProps) {
  const methods = useForm<SubscriptionFormData>({
    defaultValues: {
      platform:
        selectedPlan?.productPlatform ||
        initialData?.plan?.productPlatform ||
        (mode === "edit" ? ProductPlatform.OctopusCloud : ""),
      planId: initialData?.planId || "",
      plan: selectedPlan || initialData?.plan || undefined,
      friendlyName: initialData?.friendlyName || "",
      user: initialData?.ownerUser
        ? {
            id: initialData.ownerUser.id,
            email: initialData.ownerUser.email ?? undefined,
            firstName: initialData.ownerUser.firstName ?? undefined,
            lastName: initialData.ownerUser.lastName ?? undefined,
          }
        : undefined,
      termEndDate: initialData?.termEndDate ? DateTime.fromISO(initialData.termEndDate, { zone: "utc" }) : undefined,
      entitlements: {},
      createLifecycleType: createLifecycleType || LifecycleType.Evergreen,
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const selectedPlatform = watch("platform");
  const selectedPlanFromForm = watch("plan");
  const selectedUser = watch("user");
  const currentCreateLifecycleType = watch("createLifecycleType");
  const currentComplimentaryType = watch("complimentaryType");
  const dnsPrefix = watch("dnsPrefix");

  const canCreateNewUser =
    mode === "create" &&
    (currentCreateLifecycleType === LifecycleType.Trial ||
      (currentCreateLifecycleType === LifecycleType.Complimentary &&
        currentComplimentaryType === ComplimentaryType.Temporary));

  const isPlatformMissing = mode === "create" && !selectedPlatform;

  const effectiveLockedTier = getEffectiveLockedTier(mode, currentCreateLifecycleType, lockedTier);
  const effectiveLifecycleType =
    mode === "create" ? currentCreateLifecycleType || LifecycleType.Evergreen : lifecycleType;

  // Fetch trial entitlement overrides from the API
  const [trialOverrides, setTrialOverrides] = useState<
    { cloud: EntitlementConfiguration; server: EntitlementConfiguration } | undefined
  >(undefined);
  useEffect(() => {
    if (mode !== "create") return;
    let cancelled = false;
    SubscriptionService.getTrialEntitlements()
      .then((response) => {
        if (!cancelled) setTrialOverrides(response);
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to fetch trial entitlement overrides:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const effectivePlan = useMemo(() => {
    const basePlan = selectedPlanFromForm || selectedPlan;
    if (!basePlan) return undefined;
    if (
      mode === "create" &&
      currentCreateLifecycleType === LifecycleType.Trial &&
      trialOverrides &&
      (selectedPlatform === ProductPlatform.OctopusCloud || selectedPlatform === ProductPlatform.OctopusServer)
    ) {
      return applyTrialOverrides(basePlan, selectedPlatform, trialOverrides);
    }
    return basePlan;
  }, [selectedPlanFromForm, selectedPlan, mode, currentCreateLifecycleType, selectedPlatform, trialOverrides]);

  const [userSelectorMode, setUserSelectorMode] = useState<"search" | "create">("search");
  const [initialOwnerId] = useState<string | undefined>(initialData?.ownerUserId);
  const [ownerHasChanged, setOwnerHasChanged] = useState(false);
  const [friendlyNameTouched, setFriendlyNameTouched] = useState(false);

  // Auto-populate friendly name from DNS prefix for cloud subscriptions
  useEffect(() => {
    if (mode === "create" && selectedPlatform === ProductPlatform.OctopusCloud && !friendlyNameTouched && dnsPrefix) {
      setValue("friendlyName", dnsPrefix);
    }
  }, [mode, selectedPlatform, dnsPrefix, friendlyNameTouched, setValue]);

  // Track if the owner has changed in edit mode
  useEffect(() => {
    if (mode === "edit" && initialOwnerId) {
      setOwnerHasChanged(selectedUser?.id !== initialOwnerId);
    }
  }, [selectedUser, initialOwnerId, mode]);

  // Fetch the plan for the locked tier when set
  const [lockedPlan, setLockedPlan] = useState<Plan | undefined>(undefined);
  const [lockedPlanLoading, setLockedPlanLoading] = useState(false);
  useEffect(() => {
    if (!effectiveLockedTier || !selectedPlatform) {
      setLockedPlan(undefined);
      return;
    }
    let cancelled = false;
    setLockedPlanLoading(true);
    PlanService.getPlans({ platform: selectedPlatform, tier: effectiveLockedTier, pageSize: 1 })
      .then((response) => {
        if (!cancelled && response.items?.[0]) {
          const plan = response.items[0];
          setLockedPlan(plan);
          setValue("plan", plan);
          setValue("planId", plan.id);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to fetch plan for locked tier:", err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLockedPlanLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveLockedTier, selectedPlatform, setValue]);

  const hasPlanChanged = mode === "edit" && selectedPlanFromForm?.id !== initialData?.planId;

  // Check if lifecycle supports entitlement overrides
  const supportsOverrides = lifecycleSupportsEntitlementOverrides(initialData?.lifecycleType);

  const isFormDisabled = disabled || loading || isSubmitting || lockedPlanLoading;

  const onFormSubmit = async (data: SubscriptionFormData) => {
    const effectivePlan = data.plan || lockedPlan;
    const submitData = {
      ...data,
      plan: effectivePlan,
      planId: effectivePlan?.id || data.planId,
    };
    await onSubmit(submitData);
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {mode === "create" ? "Create New Subscription" : "Edit Subscription"}
        </Typography>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Grid container spacing={3}>
                {/* Basic Information Section */}
                <Grid size={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  {mode === "create" ? (
                    <TextField
                      fullWidth
                      select
                      required
                      label="Platform"
                      {...register("platform", {
                        required: "Platform is required",
                        onChange: () => {
                          // Clear selected plan when platform changes
                          setValue("plan", undefined);
                          setValue("planId", "");
                        },
                      })}
                      error={!!errors.platform}
                      helperText={errors.platform?.message}
                      disabled={isFormDisabled}
                    >
                      <MenuItem value="" disabled>
                        Select a platform
                      </MenuItem>
                      <MenuItem value={ProductPlatform.OctopusCloud}>Cloud</MenuItem>
                      <MenuItem value={ProductPlatform.OctopusServer}>Self-Hosted</MenuItem>
                    </TextField>
                  ) : (
                    <Box sx={{ position: "relative" }}>
                      <Tooltip title="Platform cannot be changed after creation">
                        <LockIcon
                          fontSize="small"
                          sx={{
                            color: "text.secondary",
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            right: 12,
                            zIndex: 1,
                          }}
                        />
                      </Tooltip>
                      <TextField fullWidth label="Platform" value={formatPlatform(selectedPlatform)} disabled />
                    </Box>
                  )}
                </Grid>

                {mode === "create" && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      fullWidth
                      select
                      label="Subscription Type"
                      {...register("createLifecycleType", {
                        onChange: (e) => {
                          setValue("plan", undefined);
                          setValue("planId", "");
                          setValue("user", undefined);
                          onCreateLifecycleTypeChange?.(e.target.value);
                        },
                      })}
                      disabled={isFormDisabled || isPlatformMissing}
                    >
                      <MenuItem value={LifecycleType.Evergreen}>Free</MenuItem>
                      <MenuItem value={LifecycleType.Trial}>Trial</MenuItem>
                      <MenuItem value={LifecycleType.Paid}>Paid</MenuItem>
                      <MenuItem value={LifecycleType.Complimentary}>Complimentary</MenuItem>
                    </TextField>
                  </Grid>
                )}

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  {mode === "create" && effectiveLockedTier ? (
                    <TextField fullWidth label="Plan" value={lockedPlan?.name || effectiveLockedTier} disabled />
                  ) : mode === "create" && isPlatformMissing ? (
                    <TextField fullWidth label="Plan" value="" disabled helperText="Select a platform first" />
                  ) : mode === "create" || initialData?.lifecycleType === "Complimentary" ? (
                    <PlanSelector
                      name="plan"
                      label="Plan"
                      required
                      control={methods.control}
                      platform={selectedPlatform || undefined}
                      allowedTiers={
                        mode === "create" &&
                        (currentCreateLifecycleType === LifecycleType.Paid ||
                          currentCreateLifecycleType === LifecycleType.Complimentary)
                          ? billableTiers
                          : undefined
                      }
                    />
                  ) : (
                    <Box sx={{ position: "relative" }}>
                      <Tooltip
                        title={
                          initialData?.lifecycleType === LifecycleType.Paid
                            ? 'Use the "Change plan" action on the subscription details page to change the plan'
                            : "Plan cannot be changed for this subscription type"
                        }
                      >
                        <LockIcon
                          fontSize="small"
                          sx={{
                            color: "text.secondary",
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            right: 12,
                            zIndex: 1,
                          }}
                        />
                      </Tooltip>
                      <TextField
                        fullWidth
                        label="Plan"
                        value={selectedPlanFromForm?.name || initialData?.plan?.name || "Unknown Plan"}
                        disabled
                      />
                    </Box>
                  )}
                </Grid>

                {mode === "edit" && effectiveLifecycleType && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField fullWidth label="Lifecycle Type" value={effectiveLifecycleType} disabled />
                  </Grid>
                )}

                {mode === "edit" && supportsOverrides && hasPlanChanged && (
                  <Grid size={12}>
                    <Alert severity="info" icon={<WarningIcon />}>
                      <strong>Changing to {selectedPlanFromForm?.tier} tier:</strong>
                      <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                        <li>
                          {selectedPlanFromForm?.clearsEntitlementOverrides
                            ? "Your current entitlement overrides will be cleared, because this plan grants the maximum of every limit"
                            : "Your current entitlement overrides will be automatically preserved"}
                        </li>
                        <li>
                          The plan's base entitlements will be updated to match the new {selectedPlanFromForm?.tier}{" "}
                          tier
                        </li>
                        <li>
                          After saving, you can customize entitlement values using the{" "}
                          <strong>"Edit Entitlement Overrides"</strong> button on the subscription details page
                        </li>
                      </ul>
                    </Alert>
                  </Grid>
                )}

                {/* Platform-specific fields (only shown during creation) */}
                {mode === "create" && selectedPlatform === ProductPlatform.OctopusCloud && <CloudPlatformFields />}

                {mode === "create" && selectedPlatform && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Friendly Name"
                      {...register("friendlyName", {
                        maxLength: {
                          value: 255,
                          message: "Friendly name cannot exceed 255 characters",
                        },
                        onChange: () => setFriendlyNameTouched(true),
                      })}
                      error={!!errors.friendlyName}
                      helperText={errors.friendlyName?.message}
                      disabled={isFormDisabled}
                      slotProps={{
                        inputLabel: { shrink: !!watch("friendlyName") },
                      }}
                    />
                  </Grid>
                )}

                {mode === "create" && currentCreateLifecycleType === LifecycleType.Complimentary && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <TextField
                        fullWidth
                        select
                        required
                        label="Complimentary Type"
                        {...register("complimentaryType", { required: "Complimentary Type is required" })}
                        error={!!errors.complimentaryType}
                        helperText={errors.complimentaryType?.message}
                        disabled={isFormDisabled}
                        defaultValue=""
                      >
                        <MenuItem value="" disabled>
                          Select a type
                        </MenuItem>
                        <MenuItem value={ComplimentaryType.Infrastructure}>Infrastructure</MenuItem>
                        <MenuItem value={ComplimentaryType.Temporary}>Temporary</MenuItem>
                        <MenuItem value={ComplimentaryType.Staff}>Staff</MenuItem>
                        <MenuItem value={ComplimentaryType.Partner}>Partner</MenuItem>
                      </TextField>
                    </Grid>

                    {currentComplimentaryType === ComplimentaryType.Infrastructure && (
                      <Grid
                        size={{
                          xs: 12,
                          md: 6,
                        }}
                      >
                        <TextField
                          fullWidth
                          select
                          label="Auto-Renewal Period"
                          {...register("autoRenewPeriod")}
                          disabled={isFormDisabled}
                          defaultValue=""
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value={AutoRenewPeriod.Monthly}>Monthly</MenuItem>
                          <MenuItem value={AutoRenewPeriod.Annually}>Annually</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                  </>
                )}

                <Grid
                  size={{
                    xs: 12,
                    md: userSelectorMode === "create" ? 12 : 6,
                  }}
                >
                  <UserSelector
                    name="user"
                    label="Subscription Owner"
                    control={methods.control}
                    required
                    disabled={isPlatformMissing || isFormDisabled}
                    allowCreate={canCreateNewUser}
                    onModeChange={setUserSelectorMode}
                  />
                </Grid>

                {mode === "create" && currentCreateLifecycleType === LifecycleType.Trial && (
                  <>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <TextField
                        fullWidth
                        required
                        label="Company"
                        {...register("company", { required: "Company is required" })}
                        error={!!errors.company}
                        helperText={errors.company?.message}
                        disabled={isFormDisabled}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <TextField fullWidth label="Phone" {...register("phone")} disabled={isFormDisabled} />
                    </Grid>
                  </>
                )}

                {mode === "create" && currentCreateLifecycleType === LifecycleType.Paid && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      fullWidth
                      required
                      label="Zuora Subscription Key"
                      {...register("zuoraSubscriptionKey", {
                        required: "Zuora Subscription Key is required",
                        pattern: {
                          value: ZUORA_KEY_REGEX,
                          message: ZUORA_KEY_VALIDATION_MESSAGE,
                        },
                        maxLength: {
                          value: 255,
                          message: "Cannot exceed 255 characters",
                        },
                      })}
                      error={!!errors.zuoraSubscriptionKey}
                      helperText={errors.zuoraSubscriptionKey?.message}
                      disabled={isFormDisabled}
                      placeholder="e.g. SUB12345678"
                    />
                  </Grid>
                )}

                {mode === "create" &&
                  (currentCreateLifecycleType === LifecycleType.Paid ||
                    (currentCreateLifecycleType === LifecycleType.Complimentary && currentComplimentaryType)) && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <FormDatePicker
                        name="termEndDate"
                        label="Term End Date"
                        minDate={DateTime.utc().startOf("day")}
                        maxDate={DateTime.utc().startOf("day").plus({ years: 1 }).minus({ days: 1 })}
                        showLocalConversion={false}
                        disabled={isFormDisabled}
                        variant="outlined"
                        rules={{
                          required: { value: true, message: "Term end date is required." },
                          validate: {
                            validDate: (v: DateTime) => v?.isValid || "Please enter a valid date.",
                            futureDate: (v: DateTime) => {
                              if (!v?.isValid) return true;
                              return (
                                v.startOf("day") > DateTime.utc().startOf("day") ||
                                "Term end date must be in the future."
                              );
                            },
                            maxDate: (v: DateTime) => {
                              if (!v?.isValid) return true;
                              const maxAllowed = DateTime.utc().startOf("day").plus({ years: 1 });
                              return (
                                v.toMillis() <= maxAllowed.toMillis() ||
                                "Term end date must be within 12 months from today."
                              );
                            },
                          },
                        }}
                      />
                    </Grid>
                  )}

                {mode === "create" && selectedPlatform === ProductPlatform.OctopusCloud && (
                  <CloudMaintenanceWindowFields />
                )}

                {mode === "edit" && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Friendly Name"
                      {...register("friendlyName", {
                        maxLength: {
                          value: 255,
                          message: "Friendly name cannot exceed 255 characters",
                        },
                        validate: (value) => {
                          // If the subscription had a friendly name, prevent clearing it
                          if (initialData?.friendlyName && !value?.trim()) {
                            return "Friendly name cannot be cleared once set";
                          }
                          return true;
                        },
                      })}
                      error={!!errors.friendlyName}
                      helperText={errors.friendlyName?.message}
                      disabled={isFormDisabled}
                      placeholder="No friendly name set"
                    />
                  </Grid>
                )}

                {mode === "edit" && ownerHasChanged && initialData?.id && (
                  <Grid size={12}>
                    <Alert severity="warning" icon={<WarningIcon />}>
                      Changing the subscription owner may require updating the <strong>User Access page</strong> to
                      ensure the new owner has appropriate permissions.
                    </Alert>
                  </Grid>
                )}

                {initialData?.termStartDate && (
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <Tooltip title="Term dates are managed by the system">
                        <LockIcon
                          fontSize="small"
                          sx={{
                            color: "text.secondary",
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            right: 12,
                            zIndex: 1,
                          }}
                        />
                      </Tooltip>
                      <TextField
                        fullWidth
                        label="Term Start Date"
                        value={new Date(initialData.termStartDate).toLocaleDateString()}
                        disabled
                      />
                    </Box>
                  </Grid>
                )}

                {initialData?.termEndDate && (
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                  >
                    {mode === "edit" && initialData?.isTermEndDateEditable ? (
                      <FormDatePicker
                        name="termEndDate"
                        label="Term End Date"
                        showLocalConversion={false}
                        disabled={isFormDisabled}
                        rules={{
                          required: { value: true, message: "Term end date is required." },
                          validate: {
                            validDate: (v: DateTime) => v?.isValid || "Please enter a valid date.",
                            afterStartDate: (v: DateTime) => {
                              if (!v?.isValid || !initialData?.termStartDate) return true;
                              const startDate = DateTime.fromISO(initialData.termStartDate, { zone: "utc" });
                              return v >= startDate || "Term end date must be on or after term start date.";
                            },
                          },
                        }}
                      />
                    ) : (
                      <Box sx={{ position: "relative" }}>
                        <Tooltip title="Term dates are managed by the system">
                          <LockIcon
                            fontSize="small"
                            sx={{
                              color: "text.secondary",
                              position: "absolute",
                              top: "50%",
                              transform: "translateY(-50%)",
                              right: 12,
                              zIndex: 1,
                            }}
                          />
                        </Tooltip>
                        <TextField
                          fullWidth
                          label="Term End Date"
                          value={new Date(initialData.termEndDate).toLocaleDateString()}
                          disabled
                        />
                      </Box>
                    )}
                  </Grid>
                )}

                {/* Entitlements Section - hide until a plan is available */}
                {(effectivePlan || lockedPlan) && (
                  <Grid size={12}>
                    <EntitlementEditor
                      selectedPlan={effectivePlan || lockedPlan}
                      selectedPlatform={selectedPlatform || undefined}
                      disabled={mode === "edit" || isFormDisabled}
                    />
                  </Grid>
                )}

                {/* Entitlement Overrides Section (Edit mode only, for lifecycles that support overrides) */}
                {mode === "edit" &&
                  supportsOverrides &&
                  (() => {
                    // Extract entitlement overrides - subscription types with overrides have this property
                    type SubscriptionWithOverrides = { entitlementOverrides?: EntitlementConfiguration };
                    const hasOverrides = (data: unknown): data is SubscriptionWithOverrides =>
                      typeof data === "object" && data !== null && "entitlementOverrides" in data;

                    const overrides: EntitlementConfiguration | undefined = hasOverrides(initialData)
                      ? initialData.entitlementOverrides
                      : undefined;

                    return (
                      <>
                        <Grid size={12}>
                          <Divider sx={{ my: 2 }} />
                        </Grid>
                        <Grid size={12}>
                          <OverridesDisplay overrides={overrides} />
                        </Grid>
                      </>
                    );
                  })()}

                {/* Action Buttons */}
                <Grid size={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button variant="outlined" onClick={onCancel} startIcon={<CancelIcon />} disabled={isFormDisabled}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={isFormDisabled}>
                      {isSubmitting
                        ? mode === "create"
                          ? "Creating..."
                          : "Saving..."
                        : mode === "create"
                        ? "Create Subscription"
                        : "Save Changes"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </FormProvider>
  );
}
